import { handle } from 'redux-pack';

import {
	CREATE_INVITATION_LEGACY,
	ADD_INVITATIONS,
	CREATE_INVITATIONS,
	REVOKE_INVITATION,
	LOAD_INVITATIONS,
	SET_SHOULD_SEND_APP_PASSCODE,
	SET_INVITATIONS_TAB_OPEN,
	CHANGE_INVITATION_TYPE,
	ADD_JOIN_REQUEST,
	REMOVE_JOIN_REQUEST,
	SET_JOIN_REQUEST,
	GET_PENDING_JOIN_REQUESTS,
	UPDATE_INVITATIONS,
	UPDATE_JOIN_REQUESTS,
	CLEAR_INVITATIONS,
	REMOVE_JOIN_REQUEST_BY_EMAIL,
	UPDATE_INVITATION,
	GET_INVITE_SSO_DOMAINS,
	INVITE_PROFILE_EDITED
} from '../actions/invitations';
import type { UpdateInvitationResponse } from '../types/connection';
import { ChannelAuthMethod } from '../types/types';
import { decodeHtmlEntities } from '../../utils/utils';
import type {
	Profile,
	Invitation,
	InvitationsState,
} from '../types/invitations';

interface Action {
	type: string;
	payload: any;
}

export const initialInvitationsState: InvitationsState = {
	invitations: [],
	invitationsLoading: false,
	invitationsLoaded: false,
	invitationsError: '',
	creatingInvitation: false,
	createInvitationError: '',
	revokeInvitationError: '',
	revokingInvitation: false,
	changingInvitation: false,
	shouldSendAppPasscode: false,
	invitationsTabOpen: false,
	joinRequests: [],
	joinRequest: null,
	updInvLoading: false,
	updInvLoaded: false,
	updInvError: '',
	ssoOnlyDomains: null,
	ssoOnlyDomainsLoading: false,
	ssoOnlyDomainsLoaded: false,
	ssoOnlyDomainsError: '',
};

const actions: Map<any, Function> = new Map([
	[
		CREATE_INVITATION_LEGACY,
		(state: InvitationsState, action: Action): InvitationsState => handle(state, action, {
			start: (startState) => ({ ...startState, creatingInvitation: true }),
			finish: (finishState) => ({ ...finishState, creatingInvitation: false }),
			failure: (failureState) => ({
				...failureState,
				createInvitationError: action.payload,
			}),
			success: (successState) => ({
				...successState,
				invitations: [...successState.invitations, action.payload],
				createInvitationError: '',
			}),
		}),
	],
	[
		ADD_INVITATIONS,
		(state: InvitationsState, action: Action): InvitationsState => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			return {
				...state,
				invitations: [...state.invitations, ...JSON.parse(sanitizedPayload)],
			};
		},
	],
	[
		CREATE_INVITATIONS,
		(state: InvitationsState, action: Action): InvitationsState => handle(state, action, {
			start: (startState) => ({ ...startState, creatingInvitation: true }),
			finish: (finishState) => ({ ...finishState, creatingInvitation: false }),
			failure: (failureState) => ({
				...failureState,
				createInvitationError: action.payload,
			}),
			success: (successState) => {
				const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
				return {
					...successState,
					invitations: [...successState.invitations, ...JSON.parse(sanitizedPayload)],
					createInvitationError: '',
				};
			},
		}),
	],
	[REVOKE_INVITATION, (state: InvitationsState, action: Action): InvitationsState => (
		handle(state, action, {
			start: (revInvStartState) => ({ ...revInvStartState, revokingInvitation: true }),
			finish: (revInvFinishState) => ({ ...revInvFinishState, revokingInvitation: false }),
			failure: (revInvFailState) => ({ ...revInvFailState, revokeInvitationError: action.payload }),
			success: (revInvSuccessState) => ({
				...revInvSuccessState,
				invitations: revInvSuccessState.invitations.filter(
					(invite: Invitation) => invite.profile.email !== action.payload,
				),
				revokeInvitationError: '',
			}),
		})
	)],
	[LOAD_INVITATIONS, (state: InvitationsState, action: Action): InvitationsState => (
		handle(state, action, {
			success: (loadInvSuccessState) => {
				const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
				return {
					...loadInvSuccessState,
					invitations: JSON.parse(sanitizedPayload),
				};
			},
			failure: (loadInvFailureState) => ({
				...loadInvFailureState,
				invitationsError: action.payload,
			}),
			start: (loadInvStartState) => ({
				...loadInvStartState,
				invitationsLoading: true,
			}),
			finish: (loadInvFinishState) => ({
				...loadInvFinishState,
				invitationsLoading: false,
				invitationsLoaded: true,
			}),
		})
	)],
	[CLEAR_INVITATIONS, (state: InvitationsState) => ({ ...state, invitations: [] })],
	[GET_PENDING_JOIN_REQUESTS, (state: InvitationsState, action: Action): InvitationsState => (
		handle(state, action, {
			success: (pendingSuccessState) => ({ ...pendingSuccessState, joinRequests: action.payload }),
		})
	)],
	[SET_SHOULD_SEND_APP_PASSCODE, (state: InvitationsState, action: Action): InvitationsState => (
		{ ...state, shouldSendAppPasscode: action.payload }
	)],
	[SET_INVITATIONS_TAB_OPEN, (state: InvitationsState, action: Action): InvitationsState => (
		{ ...state, invitationsTabOpen: action.payload }
	)],
	[CHANGE_INVITATION_TYPE, (state: InvitationsState, action: Action): InvitationsState => (
		handle(state, action, {
			start: (changeInvStartState) => ({ ...changeInvStartState, changingInvitation: true }),
			finish: (changeInvFinishState) => ({ ...changeInvFinishState, changingInvitation: false }),
			success: (changeInvSuccessState) => ({
				...changeInvSuccessState,
				invitations: changeInvSuccessState.invitations.map((invite: Invitation) => {
					if (invite.code === action.payload.code) {
						// eslint-disable-next-line no-param-reassign
						invite.watcher_only = !invite.watcher_only;
					}

					return invite;
				}),
			}),
		})
	)],
	[ADD_JOIN_REQUEST, (state: InvitationsState, action: Action): InvitationsState => (
		{ ...state, joinRequests: [...state.joinRequests, action.payload] }
	)],
	[REMOVE_JOIN_REQUEST, (state: InvitationsState, action: Action): InvitationsState => (
		{
			...state,
			joinRequests: state.joinRequests.filter((request: Invitation) => (
				request.code !== action.payload.code
			)),
		}
	)],
	[REMOVE_JOIN_REQUEST_BY_EMAIL, (state: InvitationsState, action: Action): InvitationsState => (
		{
			...state,
			joinRequests: state.joinRequests.filter((request: Invitation) => (
				request.profile.email !== action.payload
			)),
		}
	)],
	[SET_JOIN_REQUEST, (state: InvitationsState, action: Action): InvitationsState => (
		{ ...state, joinRequest: action.payload }
	)],
	[UPDATE_INVITATIONS, (state: InvitationsState, action: Action): InvitationsState => {
		const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
		return { ...state, invitations: JSON.parse(sanitizedPayload) };
	}],
	[UPDATE_JOIN_REQUESTS, (state: InvitationsState, action: Action): InvitationsState => ({
		...state,
		joinRequests: state.joinRequests.map((request: Invitation) => {
			if (request.code === action.payload.code) request.join_request = action.payload.join_request;
			return request;
		}),
	})],
	[
		UPDATE_INVITATION,
		(state: InvitationsState, action: Action): InvitationsState => handle(state, action, {
			start: (updInvStartState) => ({
				...updInvStartState,
				updInvLoading: true,
				updInvLoaded: false,
				updInvError: '',
			}),
			finish: (updInvFinishState) => ({
				...updInvFinishState,
				updInvLoading: false,
			}),
			failure: (updInvFailureState) => ({
				...updInvFailureState,
				updInvError: 'Failed to update invitation',
			}),
			success: (updInvSuccessState) => ({
				...updInvSuccessState,
				updInvLoaded: true,
				invitations: state.invitations.map((invite: Invitation) => {
					if (!action?.payload?.invitationCode) {
						return invite;
					}

					const {
						invitationCode,
						isWatcherOnly,
						authType,
					} = action.payload as UpdateInvitationResponse;
					const authChanged = authType && authType !== invite.authentication;
					const watcherChanged = isWatcherOnly !== null && isWatcherOnly !== invite.watcher_only;
					const hasUpdate = invitationCode === invite.code && (authChanged || watcherChanged);

					if (hasUpdate) {
						return {
							...invite,
							...(authChanged && { authentication: authType }),
							...(watcherChanged && { watcher_only: isWatcherOnly }),
						};
					}

					return invite;
				}),
			}),
		}),
	],
	[
		GET_INVITE_SSO_DOMAINS,
		(state: InvitationsState, action: Action): InvitationsState => handle(state, action, {
			start: (getSsoOnlyStartState) => ({
				...getSsoOnlyStartState,
				ssoOnlyDomainsLoading: true,
				ssoOnlyDomainsLoaded: false,
				ssoOnlyDomainsError: '',
			}),
			finish: (getSsoOnlyFinishState) => ({
				...getSsoOnlyFinishState,
				ssoOnlyDomainsLoading: false,
			}),
			failure: (getSsoOnlyFailureState) => ({
				...getSsoOnlyFailureState,
				ssoOnlyDomainsError: 'Failed to update SSO-only domains.',
			}),
			success: (getSsoOnlySuccessState) => ({
				...getSsoOnlySuccessState,
				ssoOnlyDomainsLoaded: true,
				ssoOnlyDomains: action.payload ?? null,
			}),
		}),
	],
	[INVITE_PROFILE_EDITED, (state: InvitationsState, action: Action) => {
		return {
			...state,
			invitations: state.invitations.map((inv: Invitation) => {
				if (inv.profile.gr_user === action.payload?.gr_user) {
					return { 
						...inv, 
						profile: {
							...inv.profile, 
							...action.payload,
							poster: action.payload?.bg_image ?? action.payload?.poster ?? inv?.profile?.poster,
						}
					};
				}
				return inv;
			})
		};
	}],
]);

export function InvitationsReducer(
	// eslint-disable-next-line default-param-last
	state: InvitationsState = initialInvitationsState,
	action: Action,
) {
	return actions.get(action.type)?.(state, action) || state;
}
